/**
 * Art Finder — hard-filter + soft-score consultation quiz (client).
 * Scoring: window.ArtFinderSchema from assets/art-finder-schema.js
 * (source of truth: scripts/lib/art-finder-schema.js).
 */
(function (root) {
  'use strict';

  var METRIC_NAME = 'Art Finder Completed';
  var KLAVIYO_REVISION = '2024-10-15';
  var DEFAULT_COMPANY_ID = 'Wy9UTz';
  var DEFAULT_LIST_ID = 'XWC2KN';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var SESSION_KEY = 'art-finder-session-v2';
  var STORAGE_EMAIL_KEY = 'art-finder-email';
  var HISTORY_KEY = 'gwfineArtFinder';

  function currentPageUrl(hash) {
    var loc = root.location;
    if (!loc) return '';
    var base = String(loc.pathname || '') + String(loc.search || '');
    if (!hash) return base;
    return base + '#' + hash;
  }

  function hashForView(view) {
    if (!view || !view.step || view.step === 'landing') return '';
    if (view.step === 'quiz') return 'q/' + (view.questionIndex || 0);
    if (view.step === 'results') return 'results';
    if (view.step === 'gate') return 'gate';
    return '';
  }

  function viewFromHash(hash) {
    var h = String(hash || '').replace(/^#/, '');
    if (!h) return { step: 'landing', questionIndex: 0 };
    if (h === 'results') return { step: 'results', questionIndex: 0 };
    if (h === 'gate') return { step: 'gate', questionIndex: 0 };
    var m = /^q\/(\d+)$/.exec(h);
    if (m) return { step: 'quiz', questionIndex: parseInt(m[1], 10) || 0 };
    return { step: 'landing', questionIndex: 0 };
  }

  function readAfHistory(histState) {
    if (!histState || typeof histState !== 'object') return null;
    var view = histState[HISTORY_KEY];
    if (!view || typeof view !== 'object' || !view.step) return null;
    return view;
  }

  function writeAfHistory(histState, view) {
    var base = histState && typeof histState === 'object' ? Object.assign({}, histState) : {};
    base[HISTORY_KEY] = {
      v: 1,
      step: view.step,
      questionIndex: view.questionIndex || 0,
      gateResumeIndex: view.gateResumeIndex == null ? null : view.gateResumeIndex,
      answers: Object.assign({}, view.answers || {}),
      relax_subject: !!view.relax_subject
    };
    return base;
  }

  function sameAfHistoryView(a, b) {
    if (!a || !b) return false;
    if (a.step !== b.step) return false;
    if ((a.questionIndex || 0) !== (b.questionIndex || 0)) return false;
    var ag = a.gateResumeIndex == null ? null : a.gateResumeIndex;
    var bg = b.gateResumeIndex == null ? null : b.gateResumeIndex;
    if (ag !== bg) return false;
    if (!!a.relax_subject !== !!b.relax_subject) return false;
    var aa = a.answers || {};
    var ba = b.answers || {};
    var keys = Object.keys(aa);
    if (keys.length !== Object.keys(ba).length) return false;
    for (var i = 0; i < keys.length; i++) {
      if (aa[keys[i]] !== ba[keys[i]]) return false;
    }
    return true;
  }

  function schema() {
    return root.ArtFinderSchema || (typeof globalThis !== 'undefined' ? globalThis.ArtFinderSchema : null);
  }

  function trackArtFinder(eventName, props) {
    if (typeof root.gwfineTrackArtFinder === 'function') {
      root.gwfineTrackArtFinder(eventName, props || {});
      return;
    }
    if (typeof root.gwfineTrackVisualizer === 'function') {
      root.gwfineTrackVisualizer(eventName, props || {});
    }
  }

  function companyIdFromPage() {
    try {
      var scripts = document.querySelectorAll('script[src*="klaviyo"]');
      for (var i = 0; i < scripts.length; i++) {
        var src = scripts[i].getAttribute('src') || '';
        var match = src.match(/company_id=([A-Za-z0-9]+)/);
        if (match) return match[1];
      }
    } catch (e) {}
    return DEFAULT_COMPANY_ID;
  }

  function klaviyoHeaders() {
    return {
      'Content-Type': 'application/vnd.api+json',
      Accept: 'application/vnd.api+json',
      revision: KLAVIYO_REVISION
    };
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function utmFromLocation() {
    var out = {};
    try {
      var params = new URLSearchParams(root.location.search || '');
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) {
        var v = params.get(k);
        if (v) out[k] = v;
      });
    } catch (e) {}
    return out;
  }

  function readSession() {
    try {
      var raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function writeSession(session) {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } catch (e) {}
  }

  function newSession(config) {
    return {
      id: 'af-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8),
      quiz_version: (config.quiz && config.quiz.version) || '2.0.0',
      answers: {},
      started_at: null,
      completed_at: null,
      email: '',
      first_name: '',
      relax_subject: false,
      utm: utmFromLocation()
    };
  }

  function optionFor(config, questionId, optionId) {
    var questions = config.questions || [];
    for (var i = 0; i < questions.length; i++) {
      if (questions[i].id !== questionId) continue;
      var opts = questions[i].options || [];
      for (var j = 0; j < opts.length; j++) {
        if (opts[j].id === optionId) return opts[j];
      }
    }
    return null;
  }

  /** Map quiz option tags → schema softScore / hardFilter quiz shape. */
  function answersToQuiz(config, answers) {
    var quiz = {};
    var questions = config.questions || [];
    questions.forEach(function (q) {
      var answerId = answers[q.id];
      if (!answerId) return;
      var opt = optionFor(config, q.id, answerId);
      if (!opt || !opt.tags) return;
      var tags = opt.tags;
      if (tags.room != null) quiz.room = tags.room;
      if (tags.mood) quiz.mood = tags.mood;
      if (tags.subject != null && tags.subject !== '') {
        quiz.subject = tags.subject;
        quiz.subject_group = tags.subject;
      }
      if (tags.palette != null && tags.palette !== '') {
        quiz.palette = tags.palette;
        quiz.visual = tags.palette;
      }
      if (tags.statement) {
        quiz.statement = tags.statement;
        quiz.presence = tags.statement;
      }
      if (tags.wall_width) quiz.wall_width = tags.wall_width;
      if (tags.size) quiz.size = tags.size;
      if (tags.size_label) quiz.size_label = tags.size_label;
    });
    return quiz;
  }

  function mediumLabel(config, mediumKey) {
    var labels = config.medium_labels || {};
    if (mediumKey && labels[mediumKey]) return labels[mediumKey];
    if (!mediumKey) return 'Acrylic';
    return String(mediumKey)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, function (c) {
        return c.toUpperCase();
      });
  }

  function resolveMedium(product, config) {
    var list = (product && product.recommended_medium) || [];
    if (!Array.isArray(list)) list = [];
    var key = list[0] || 'acrylic';
    return { key: key, label: mediumLabel(config, key) };
  }

  function resolveSize(config, quiz) {
    var q = config.quiz || {};
    quiz = quiz || {};
    var defaults = {
      size: q.default_size || '32 x 48',
      label: q.default_size_label || '32 × 48 · starting size'
    };

    // Explicit wall-width answer wins.
    if (quiz.size) {
      return {
        size: quiz.size,
        label: quiz.size_label || quiz.size.replace(/ x /g, ' × ')
      };
    }

    // Commercial room without a wall answer → larger starting size.
    if (quiz.room === 'commercial') {
      return { size: '40 x 60', label: '40 × 60 · starting size' };
    }

    return defaults;
  }

  function visualizerUrl(routes, handle, size, mediumKey) {
    var base = (routes && routes.visualizer) || '/pages/visualize-your-space';
    var params = new URLSearchParams();
    if (handle) params.set('product', handle);
    if (size) params.set('size', String(size).replace(/\s+/g, ''));
    if (mediumKey) params.set('medium', mediumKey);
    var qs = params.toString();
    return qs ? base + '?' + qs : base;
  }

  function rowFromPick(pick, catalog, config, quiz) {
    if (!pick) return null;
    var handle = pick.handle;
    var product = catalog[handle] || pick.product || {};
    var S = schema();
    var meta = pick.meta || (S && S.normalizeProductMeta(Object.assign({}, product, { handle: handle })));
    var medium = resolveMedium(Object.assign({}, product, meta || {}), config);
    var size = resolveSize(config, quiz || {});
    var why =
      pick.why ||
      (S
        ? S.whyCopy(
            Object.assign({}, meta || {}, { title: product.title || handle, handle: handle }),
            quiz,
            pick.matchReasons
          )
        : '');
    return {
      handle: handle,
      title: product.title || handle,
      url: product.url || '/products/' + handle,
      image: product.image || '',
      mockup: product.mockup || '',
      score: pick.score,
      why: why,
      medium: medium,
      size: size,
      statement_level: (meta && meta.statement_level) || pick.statement_level || '',
      meta: meta
    };
  }

  function buildRecommendation(config, catalog, answers, opts) {
    opts = opts || {};
    var S = schema();
    if (!S) {
      return {
        quiz: {},
        eligible_count: 0,
        artworks: [],
        picks: { strongest: null, quieter: null, bolder: null },
        roles: [],
        relax_subject: !!opts.relaxSubject,
        no_results: true
      };
    }

    var quiz = answersToQuiz(config, answers || {});
    if (opts.relaxSubject) {
      quiz.subject = 'surprise';
      quiz.subject_group = 'surprise';
    }

    var eligible = [];
    Object.keys(catalog || {}).forEach(function (handle) {
      var product = catalog[handle];
      if (!product) return;
      var raw = Object.assign({}, product, { handle: handle, title: product.title || handle });
      if (!S.hardFilterEligible(raw, quiz)) return;
      var scored = S.softScore(raw, quiz);
      eligible.push({
        handle: handle,
        product: product,
        score: scored.score,
        matchReasons: scored.matchReasons,
        meta: scored.meta,
        why: S.whyCopy(
          Object.assign({}, scored.meta, { title: product.title || handle, handle: handle }),
          quiz,
          scored.matchReasons
        )
      });
    });

    var picks = S.pickRecommendations(eligible);
    var roles = [];
    var roleOrder = ['strongest', 'quieter', 'bolder'];
    var labels = (config.role_labels || {});
    roleOrder.forEach(function (role) {
      var pick = picks[role];
      if (!pick) return;
      var row = rowFromPick(pick, catalog, config, quiz);
      if (!row) return;
      row.role = role;
      row.role_label = labels[role] || role;
      roles.push(row);
    });

    var headline = 'Photographs for your space';
    if (quiz.subject && quiz.subject !== 'surprise') {
      headline = 'A close fit for what you chose';
    } else if (quiz.subject === 'surprise') {
      headline = 'A few strong places to start';
    }

    return {
      quiz: quiz,
      eligible_count: eligible.length,
      artworks: roles,
      picks: picks,
      roles: roles,
      relax_subject: !!opts.relaxSubject,
      no_results: roles.length === 0,
      headline: headline,
      subject_group: quiz.subject_group || quiz.subject || 'surprise'
    };
  }

  function subscribeAndTrack(opts) {
    var companyId = opts.companyId || companyIdFromPage();
    var listId = opts.listId || DEFAULT_LIST_ID;
    var email = opts.email;
    var firstName = opts.firstName || '';
    var props = opts.properties || {};

    var profileAttributes = {
      email: email,
      properties: Object.assign(
        {
          lead_source: 'art_finder'
        },
        props
      )
    };
    if (firstName) profileAttributes.first_name = firstName;

    var subscribeBody = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Art Finder',
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes
            }
          }
        },
        relationships: {
          list: {
            data: { type: 'list', id: listId }
          }
        }
      }
    };

    var eventBody = {
      data: {
        type: 'event',
        attributes: {
          unique_id:
            'af-' +
            Date.now() +
            '-' +
            String(email)
              .toLowerCase()
              .replace(/[^a-z0-9@._-]/g, '')
              .slice(0, 40),
          properties: Object.assign(
            {
              source: 'art-finder',
              subject_group: props.art_finder_subject_group || '',
              handles: props.art_finder_handles || ''
            },
            opts.eventProps || {}
          ),
          metric: {
            data: {
              type: 'metric',
              attributes: { name: METRIC_NAME }
            }
          },
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes
            }
          }
        }
      }
    };

    try {
      root.klaviyo = root.klaviyo || [];
      var identify = Object.assign({ $email: email, email: email }, props);
      if (firstName) identify.first_name = firstName;
      if (typeof root.klaviyo.identify === 'function') {
        root.klaviyo.identify(identify);
      } else {
        root.klaviyo.push(['identify', identify]);
      }
      if (!opts.skipMetric) {
        if (typeof root.klaviyo.track === 'function') {
          root.klaviyo.track(METRIC_NAME, opts.eventProps || {});
        } else {
          root.klaviyo.push(['track', METRIC_NAME, opts.eventProps || {}]);
        }
      }
    } catch (e) {}

    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: klaviyoHeaders(),
      body: JSON.stringify(subscribeBody)
    })
      .then(function (res) {
        if (!res || (!res.ok && res.status !== 202)) throw new Error('subscribe ' + (res && res.status));
        if (opts.skipMetric) return { ok: true, deferredMetric: true };
        return fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(companyId), {
          method: 'POST',
          headers: klaviyoHeaders(),
          body: JSON.stringify(eventBody)
        }).then(function (eventRes) {
          if (eventRes && (eventRes.ok || eventRes.status === 202)) return { ok: true };
          return { ok: true, trackFailed: true };
        });
      });
  }

  function trackCompletedMetric(opts) {
    var companyId = opts.companyId || companyIdFromPage();
    var email = opts.email;
    var firstName = opts.firstName || '';
    var props = opts.properties || {};
    var profileAttributes = {
      email: email,
      properties: Object.assign({ lead_source: 'art_finder' }, props)
    };
    if (firstName) profileAttributes.first_name = firstName;

    var eventBody = {
      data: {
        type: 'event',
        attributes: {
          unique_id:
            'af-' +
            Date.now() +
            '-' +
            String(email)
              .toLowerCase()
              .replace(/[^a-z0-9@._-]/g, '')
              .slice(0, 40),
          properties: Object.assign(
            {
              source: 'art-finder',
              subject_group: props.art_finder_subject_group || '',
              handles: props.art_finder_handles || ''
            },
            opts.eventProps || {}
          ),
          metric: {
            data: {
              type: 'metric',
              attributes: { name: METRIC_NAME }
            }
          },
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes
            }
          }
        }
      }
    };

    try {
      root.klaviyo = root.klaviyo || [];
      if (typeof root.klaviyo.track === 'function') {
        root.klaviyo.track(METRIC_NAME, opts.eventProps || {});
      } else {
        root.klaviyo.push(['track', METRIC_NAME, opts.eventProps || {}]);
      }
    } catch (e) {}

    return fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: klaviyoHeaders(),
      body: JSON.stringify(eventBody)
    }).then(function (res) {
      if (res && (res.ok || res.status === 202)) return { ok: true };
      return { ok: true, trackFailed: true };
    });
  }

  function sendMakePayload(session, rec) {
    if (typeof root.sendToMake !== 'function') return Promise.resolve({ skipped: true });
    var now = new Date().toISOString();
    var handles = (rec.artworks || [])
      .map(function (a) {
        return a.handle;
      })
      .join(',');
    var subject = rec.subject_group || '';
    var first = (rec.artworks || [])[0];
    return root.sendToMake({
      form_type: 'art-finder',
      create_date: now,
      last_update: now,
      first_name: session.first_name || '',
      email: session.email || '',
      updates: 'Yes',
      source: 'art-finder',
      interest: subject,
      size_range: first && first.size ? first.size.size : '',
      room_placement: (rec.quiz && rec.quiz.room) || '',
      product_title: handles,
      product_url: first ? first.url : '',
      timeline: '',
      notes:
        'subject_group=' +
        subject +
        '; mood=' +
        ((rec.quiz && rec.quiz.mood) || '') +
        '; statement=' +
        ((rec.quiz && rec.quiz.statement) || '') +
        '; relax=' +
        !!rec.relax_subject,
      tags: 'art-finder,' + subject,
      utm_source: (session.utm && session.utm.utm_source) || '',
      campaign: (session.utm && session.utm.utm_campaign) || '',
      art_finder_subject_group: subject,
      art_finder_handles: handles,
      art_finder_mood: (rec.quiz && rec.quiz.mood) || '',
      art_finder_room: (rec.quiz && rec.quiz.room) || ''
    });
  }

  function ArtFinderApp(rootEl, boot) {
    this.root = rootEl;
    this.config = boot.config;
    this.catalog = boot.catalog || {};
    this.images = boot.images || {};
    this.listId = boot.listId || DEFAULT_LIST_ID;
    this.companyId = boot.companyId || companyIdFromPage();
    this.routes = boot.routes || {};
    this.showGuideBonus = !!boot.showGuideBonus;
    this.session = readSession() || newSession(this.config);
    if (!this.session.email) {
      try {
        var savedEmailInit = sessionStorage.getItem(STORAGE_EMAIL_KEY);
        if (savedEmailInit) this.session.email = savedEmailInit;
      } catch (e) {}
    }
    this.step = 'landing';
    this.questionIndex = 0;
    this.gateResumeIndex = null;
    this.recommendation = null;
    this.submitting = false;
    this._abandonedSent = false;
    this._resultsTracked = false;
    this._completedTracked = false;
    this.bind();
    if (!this.restoreFromHistory()) {
      this.syncHistory('replace');
      this.render();
      trackArtFinder('art_finder_landing_view', { version: this.config.quiz.version });
    }
  }

  ArtFinderApp.prototype.bind = function () {
    var self = this;
    this.root.addEventListener('click', function (e) {
      self.onClick(e);
    });
    this.root.addEventListener('submit', function (e) {
      self.onSubmit(e);
    });
    root.addEventListener('pagehide', function () {
      self.onPageHide();
    });
    root.addEventListener('popstate', function (e) {
      self.onPopState(e);
    });
  };

  ArtFinderApp.prototype.historyView = function () {
    return {
      step: this.step,
      questionIndex: this.questionIndex || 0,
      gateResumeIndex: this.gateResumeIndex == null ? null : this.gateResumeIndex,
      answers: (this.session && this.session.answers) || {},
      relax_subject: !!(this.session && this.session.relax_subject)
    };
  };

  ArtFinderApp.prototype.syncHistory = function (mode) {
    if (!root.history || typeof root.history.pushState !== 'function') return;
    var view = this.historyView();
    var current = readAfHistory(root.history.state);
    if (mode !== 'replace' && sameAfHistoryView(current, view)) return;
    try {
      var next = writeAfHistory(root.history.state, view);
      var url = currentPageUrl(hashForView(view));
      if (mode === 'push') root.history.pushState(next, '', url);
      else root.history.replaceState(next, '', url);
    } catch (e) {}
  };

  ArtFinderApp.prototype.applyHistoryView = function (view) {
    if (!view || !view.step) return;
    if (view.answers && typeof view.answers === 'object') {
      this.session.answers = Object.assign({}, view.answers);
    }
    this.session.relax_subject = !!view.relax_subject;
    writeSession(this.session);
    this.step = view.step;
    this.questionIndex = view.questionIndex || 0;
    var qCount = this.questions().length;
    if (this.step === 'quiz' && qCount && this.questionIndex >= qCount) {
      this.questionIndex = qCount - 1;
    }
    this.gateResumeIndex = view.gateResumeIndex == null ? null : view.gateResumeIndex;
    if (this.step === 'results' || this.step === 'gate') {
      this.recommendation = this.computeRecommendation(!!this.session.relax_subject);
    }
    this.render();
  };

  ArtFinderApp.prototype.restoreFromHistory = function () {
    var fromState = readAfHistory(root.history && root.history.state);
    var fromHash = viewFromHash(root.location && root.location.hash);
    var view = fromState;
    if (!view && fromHash && fromHash.step !== 'landing') {
      view = {
        step: fromHash.step,
        questionIndex: fromHash.questionIndex || 0,
        answers: (this.session && this.session.answers) || {},
        relax_subject: !!(this.session && this.session.relax_subject),
        gateResumeIndex: null
      };
    }
    if (!view || view.step === 'landing') return false;
    var answers = view.answers || (this.session && this.session.answers) || {};
    if (view.step === 'results' && !Object.keys(answers).length) return false;
    this.applyHistoryView(view);
    this.syncHistory('replace');
    return true;
  };

  ArtFinderApp.prototype.onPopState = function (e) {
    var view = readAfHistory(e && e.state);
    if (!view) {
      view = viewFromHash(root.location && root.location.hash);
      view.answers = (this.session && this.session.answers) || {};
      view.relax_subject = !!(this.session && this.session.relax_subject);
    }
    this.applyHistoryView(view);
  };

  ArtFinderApp.prototype.showResults = function (historyMode) {
    this.step = 'results';
    this.syncHistory(historyMode || 'push');
    this.render();
  };

  ArtFinderApp.prototype.onPageHide = function () {
    if (this._abandonedSent) return;
    if (this.step === 'results' || this.step === 'landing') return;
    if (!this.session.started_at) return;
    this._abandonedSent = true;
    var q = (this.config.questions || [])[this.questionIndex];
    trackArtFinder('art_finder_abandoned', {
      last_question_id: q ? q.id : '',
      question_index: this.questionIndex
    });
  };

  ArtFinderApp.prototype.questions = function () {
    return (this.config.questions || []).slice().sort(function (a, b) {
      return (a.order || 0) - (b.order || 0);
    });
  };

  ArtFinderApp.prototype.optionImage = function (option) {
    if (!option) return '';
    if (option.product_handle && this.catalog[option.product_handle]) {
      return this.catalog[option.product_handle].image || '';
    }
    return '';
  };

  ArtFinderApp.prototype.computeRecommendation = function (relax) {
    return buildRecommendation(this.config, this.catalog, this.session.answers, {
      relaxSubject: !!relax
    });
  };

  ArtFinderApp.prototype.onClick = function (e) {
    var t = e.target.closest('[data-af-action]');
    if (!t) return;
    var action = t.getAttribute('data-af-action');
    if (action === 'start') {
      e.preventDefault();
      this.startQuiz();
      return;
    }
    if (action === 'back') {
      e.preventDefault();
      this.goBack();
      return;
    }
    if (action === 'answer') {
      e.preventDefault();
      var qid = t.getAttribute('data-question');
      var oid = t.getAttribute('data-option');
      this.selectAnswer(qid, oid);
      return;
    }
    if (action === 'restart') {
      e.preventDefault();
      this.restartKeepEmail();
      return;
    }
    if (action === 'relax') {
      e.preventDefault();
      this.relaxAndRerank();
      return;
    }
    if (action === 'track') {
      var ev = t.getAttribute('data-af-event');
      var handle = t.getAttribute('data-handle') || '';
      if (ev) trackArtFinder(ev, { handle: handle });
    }
  };

  ArtFinderApp.prototype.onSubmit = function (e) {
    var form = e.target;
    if (!form || !form.hasAttribute('data-af-gate')) return;
    e.preventDefault();
    this.submitGate(form);
  };

  ArtFinderApp.prototype.startQuiz = function () {
    if (!this.session.started_at) {
      this.session.started_at = new Date().toISOString();
      writeSession(this.session);
      trackArtFinder('art_finder_started', { version: this.config.quiz.version });
    }
    this.step = 'quiz';
    this.questionIndex = 0;
    this.syncHistory('push');
    this.render();
  };

  ArtFinderApp.prototype.selectAnswer = function (questionId, optionId) {
    this.session.answers[questionId] = optionId;
    this.session.relax_subject = false;
    writeSession(this.session);
    var answeredQuiz = answersToQuiz(this.config, this.session.answers);
    trackArtFinder('art_finder_question_answered', {
      question_id: questionId,
      answer_id: optionId,
      space_type: answeredQuiz.room || '',
      mood: answeredQuiz.mood || '',
      palette: answeredQuiz.palette || '',
      statement: answeredQuiz.statement || ''
    });
    var questions = this.questions();
    var answeredIndex = this.questionIndex;

    if (answeredIndex < questions.length - 1) {
      this.questionIndex += 1;
      this.syncHistory('push');
      this.render();
      return;
    }

    this.recommendation = this.computeRecommendation(false);
    if (this.recommendation.no_results) {
      var nrQuiz = this.recommendation.quiz || answeredQuiz;
      trackArtFinder('art_finder_no_results', {
        subject_group: this.recommendation.subject_group || '',
        space_type: nrQuiz.room || '',
        mood: nrQuiz.mood || ''
      });
    }
    this.finishToResults();
  };

  ArtFinderApp.prototype.goBack = function () {
    if (root.history && typeof root.history.back === 'function' && readAfHistory(root.history.state)) {
      root.history.back();
      return;
    }
    if (this.step === 'gate') {
      this.step = 'quiz';
      if (typeof this.gateResumeIndex === 'number') {
        this.questionIndex = Math.max(0, this.gateResumeIndex - 1);
        this.gateResumeIndex = null;
      } else {
        this.questionIndex = this.questions().length - 1;
      }
      this.syncHistory('replace');
      this.render();
      return;
    }
    if (this.step === 'quiz') {
      if (this.questionIndex === 0) {
        this.step = 'landing';
        this.syncHistory('replace');
        this.render();
        return;
      }
      this.questionIndex -= 1;
      this.syncHistory('replace');
      this.render();
    }
  };

  ArtFinderApp.prototype.restartKeepEmail = function () {
    var email = this.session.email;
    this.session = newSession(this.config);
    this.session.email = email;
    this.session.first_name = '';
    this.recommendation = null;
    this.gateResumeIndex = null;
    this._resultsTracked = false;
    this._completedTracked = false;
    this.step = 'landing';
    this.questionIndex = 0;
    writeSession(this.session);
    trackArtFinder('art_finder_restarted', { version: this.config.quiz.version });
    this.syncHistory('push');
    this.render();
  };

  ArtFinderApp.prototype.relaxAndRerank = function () {
    this.session.relax_subject = true;
    writeSession(this.session);
    this.recommendation = this.computeRecommendation(true);
    this._resultsTracked = false;
    this.showResults('push');
  };

  ArtFinderApp.prototype.isMidQuizGate = function () {
    return typeof this.gateResumeIndex === 'number' && this.gateResumeIndex < this.questions().length;
  };

  ArtFinderApp.prototype.recommendationProps = function (rec) {
    var handles = (rec.artworks || [])
      .map(function (a) {
        return a.handle;
      })
      .join(',');
    return {
      handles: handles,
      props: {
        art_finder_subject_group: rec.subject_group || '',
        art_finder_handles: handles,
        art_finder_mood: (rec.quiz && rec.quiz.mood) || '',
        art_finder_space_type: (rec.quiz && rec.quiz.room) || '',
        art_finder_statement: (rec.quiz && rec.quiz.statement) || '',
        art_finder_palette: (rec.quiz && rec.quiz.palette) || '',
        art_finder_version: (this.config.quiz && this.config.quiz.version) || '2.0.0'
      },
      eventProps: {
        subject_group: rec.subject_group || '',
        handles: handles,
        space_type: (rec.quiz && rec.quiz.room) || '',
        mood: (rec.quiz && rec.quiz.mood) || ''
      }
    };
  };

  ArtFinderApp.prototype.finishToResults = function () {
    var self = this;
    if (!this.recommendation) {
      this.recommendation = this.computeRecommendation(!!this.session.relax_subject);
    }
    var rec = this.recommendation;
    this.session.completed_at = new Date().toISOString();
    writeSession(this.session);

    if (this._completedTracked) {
      this.showResults('push');
      return;
    }
    this._completedTracked = true;

    var pack = this.recommendationProps(rec);
    var arts = rec.artworks || [];
    var eventBody = {
      subject_group: rec.subject_group || '',
      eligible_count: rec.eligible_count || 0,
      space_type: (rec.quiz && rec.quiz.room) || '',
      mood: (rec.quiz && rec.quiz.mood) || '',
      palette: (rec.quiz && rec.quiz.palette) || '',
      statement: (rec.quiz && rec.quiz.statement) || '',
      rank_1: arts[0] ? arts[0].handle : '',
      rank_2: arts[1] ? arts[1].handle : '',
      rank_3: arts[2] ? arts[2].handle : ''
    };

    if (!this.session.email) {
      trackArtFinder('art_finder_completed', eventBody);
      this.showResults('push');
      return;
    }

    Promise.resolve()
      .then(function () {
        return trackCompletedMetric({
          companyId: self.companyId,
          email: self.session.email,
          firstName: self.session.first_name || '',
          properties: pack.props,
          eventProps: pack.eventProps
        }).catch(function () {
          return null;
        });
      })
      .then(function () {
        return sendMakePayload(self.session, rec).catch(function () {
          return null;
        });
      })
      .then(function () {
        trackArtFinder('art_finder_completed', {
          subject_group: rec.subject_group || '',
          eligible_count: rec.eligible_count || 0,
          space_type: (rec.quiz && rec.quiz.room) || '',
          mood: (rec.quiz && rec.quiz.mood) || '',
          palette: (rec.quiz && rec.quiz.palette) || '',
          statement: (rec.quiz && rec.quiz.statement) || '',
          rank_1: arts[0] ? arts[0].handle : '',
          rank_2: arts[1] ? arts[1].handle : '',
          rank_3: arts[2] ? arts[2].handle : ''
        });
        self.showResults('push');
      });
  };

  ArtFinderApp.prototype.submitGate = function (form) {
    var self = this;
    if (this.submitting) return;
    var emailInput = form.querySelector('[name="email"]');
    var err = form.querySelector('[data-af-error]');
    var email = emailInput ? String(emailInput.value || '').trim() : '';
    var midQuiz = this.isMidQuizGate();
    if (err) {
      err.hidden = true;
      err.textContent = '';
    }
    if (!EMAIL_RE.test(email)) {
      if (err) {
        err.textContent = 'Please enter a valid email address.';
        err.hidden = false;
      }
      if (emailInput) emailInput.focus();
      return;
    }

    this.session.email = email;
    this.session.first_name = '';
    writeSession(this.session);
    try {
      sessionStorage.setItem(STORAGE_EMAIL_KEY, email);
    } catch (e) {}

    var answeredQuiz = answersToQuiz(this.config, this.session.answers);
    var props = {
      art_finder_mood: answeredQuiz.mood || '',
      art_finder_space_type: answeredQuiz.room || '',
      art_finder_version: (this.config.quiz && this.config.quiz.version) || '2.0.0'
    };
    var eventProps = {
      space_type: answeredQuiz.room || '',
      mood: answeredQuiz.mood || '',
      mid_quiz: midQuiz
    };

    if (!midQuiz) {
      if (!this.recommendation) {
        this.recommendation = this.computeRecommendation(!!this.session.relax_subject);
      }
      var pack = this.recommendationProps(this.recommendation);
      props = pack.props;
      eventProps = pack.eventProps;
      this.session.completed_at = new Date().toISOString();
      writeSession(this.session);
    }

    this.submitting = true;
    var btn = form.querySelector('[type="submit"]');
    if (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
    }

    trackArtFinder('art_finder_email_submitted', {
      subject_group: (!midQuiz && this.recommendation && this.recommendation.subject_group) || '',
      space_type: answeredQuiz.room || '',
      mood: answeredQuiz.mood || '',
      palette: (!midQuiz && this.recommendation && this.recommendation.quiz && this.recommendation.quiz.palette) || '',
      statement: (!midQuiz && this.recommendation && this.recommendation.quiz && this.recommendation.quiz.statement) || '',
      mid_quiz: midQuiz
    });
    if (typeof root.gwfineTrackSignup === 'function') {
      root.gwfineTrackSignup('Art Finder');
    }

    var alreadyCompleted = this._completedTracked;

    subscribeAndTrack({
      companyId: this.companyId,
      listId: this.listId,
      email: email,
      firstName: '',
      properties: props,
      eventProps: eventProps,
      skipMetric: midQuiz
    })
      .then(function () {
        if (midQuiz) return null;
        return sendMakePayload(self.session, self.recommendation).catch(function () {
          return null;
        });
      })
      .then(function () {
        self.submitting = false;
        if (midQuiz) {
          var resume = typeof self.gateResumeIndex === 'number' ? self.gateResumeIndex : 2;
          self.gateResumeIndex = null;
          self.questionIndex = resume;
          self.step = 'quiz';
          self.syncHistory('push');
          self.render();
          return;
        }
        self._completedTracked = true;
        var rec = self.recommendation;
        var arts = (rec && rec.artworks) || [];
        if (!alreadyCompleted) {
          trackArtFinder('art_finder_completed', {
            subject_group: (rec && rec.subject_group) || '',
            eligible_count: (rec && rec.eligible_count) || 0,
            space_type: (rec && rec.quiz && rec.quiz.room) || '',
            mood: (rec && rec.quiz && rec.quiz.mood) || '',
            palette: (rec && rec.quiz && rec.quiz.palette) || '',
            statement: (rec && rec.quiz && rec.quiz.statement) || '',
            rank_1: arts[0] ? arts[0].handle : '',
            rank_2: arts[1] ? arts[1].handle : '',
            rank_3: arts[2] ? arts[2].handle : ''
          });
        }
        self.showResults('push');
      })
      .catch(function () {
        self.submitting = false;
        if (btn) {
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
        }
        if (err) {
          err.textContent = 'Something went wrong. Please try again.';
          err.hidden = false;
        }
      });
  };

  ArtFinderApp.prototype.progressDots = function (activeIndex) {
    var n = this.questions().length;
    var html = '<ol class="af-progress" aria-label="Progress">';
    for (var i = 0; i < n; i++) {
      html +=
        '<li class="af-progress__dot' +
        (i === activeIndex ? ' is-active' : '') +
        (i < activeIndex ? ' is-done' : '') +
        '" aria-current="' +
        (i === activeIndex ? 'step' : 'false') +
        '"></li>';
    }
    html += '</ol>';
    return html;
  };

  ArtFinderApp.prototype.renderLanding = function () {
    var q = this.config.quiz;
    var hero = this.images.hero || (this.catalog['the-veteran'] && this.catalog['the-veteran'].image) || '';
    return (
      '<section class="af-landing">' +
      (hero
        ? '<div class="af-landing__media"><img src="' +
          escapeHtml(hero) +
          '" alt="" width="1600" height="1000" decoding="async"></div>'
        : '') +
      '<div class="af-landing__copy">' +
      '<p class="af-eyebrow">Art Finder</p>' +
      '<h1 class="af-heading af-heading--hero">' +
      escapeHtml(q.headline) +
      '</h1>' +
      '<p class="af-lede">' +
      escapeHtml(q.supporting) +
      '</p>' +
      '<button type="button" class="af-btn af-btn--primary" data-af-action="start">' +
      escapeHtml(q.cta_start) +
      '</button>' +
      '</div></section>'
    );
  };

  ArtFinderApp.prototype.renderOption = function (question, option, selectedId) {
    var selected = selectedId === option.id;
    var img = this.optionImage(option);
    var visual = question.visual;
    var classes = 'af-option af-option--' + escapeHtml(visual) + (selected ? ' is-selected' : '');
    var inner = '';

    if (visual === 'swatch' && option.swatch) {
      inner += '<span class="af-swatch" aria-hidden="true">';
      option.swatch.forEach(function (c) {
        inner += '<span class="af-swatch__chip" style="background:' + escapeHtml(c) + '"></span>';
      });
      inner += '</span>';
    } else if (img) {
      inner +=
        '<span class="af-option__media"><img src="' +
        escapeHtml(img) +
        '" alt="' +
        escapeHtml(option.label) +
        '" loading="lazy" decoding="async"></span>';
    } else if (visual === 'icon' || visual === 'size') {
      inner += '<span class="af-option__icon" aria-hidden="true"></span>';
    }

    inner += '<span class="af-option__label">' + escapeHtml(option.label) + '</span>';

    return (
      '<button type="button" role="radio" aria-checked="' +
      (selected ? 'true' : 'false') +
      '" class="' +
      classes +
      '" data-af-action="answer" data-question="' +
      escapeHtml(question.id) +
      '" data-option="' +
      escapeHtml(option.id) +
      '">' +
      inner +
      '</button>'
    );
  };

  ArtFinderApp.prototype.renderQuiz = function () {
    var questions = this.questions();
    var question = questions[this.questionIndex];
    if (!question) return '';
    var selectedId = this.session.answers[question.id] || '';
    var opts = (question.options || [])
      .map(
        function (o) {
          return this.renderOption(question, o, selectedId);
        }.bind(this)
      )
      .join('');

    return (
      '<section class="af-quiz">' +
      '<div class="af-quiz__bar">' +
      '<button type="button" class="af-back" data-af-action="back" aria-label="Back">←</button>' +
      this.progressDots(this.questionIndex) +
      '</div>' +
      '<h2 class="af-heading af-heading--md" id="af-q-label">' +
      escapeHtml(question.prompt) +
      '</h2>' +
      (question.supporting
        ? '<p class="af-supporting">' + escapeHtml(question.supporting) + '</p>'
        : '') +
      '<div class="af-options af-options--' +
      escapeHtml(question.visual) +
      '" role="radiogroup" aria-labelledby="af-q-label">' +
      opts +
      '</div></section>'
    );
  };

  ArtFinderApp.prototype.renderGate = function () {
    var q = this.config.quiz;
    var midQuiz = this.isMidQuizGate();
    var peekImg = '';
    var peekAlt = '';
    var teaserTitle = '';

    if (!midQuiz) {
      var rec =
        this.recommendation || this.computeRecommendation(!!this.session.relax_subject);
      this.recommendation = rec;
      var first = (rec.artworks || [])[0];
      peekImg =
        (first && (first.image || first.mockup)) ||
        this.images.hero ||
        '';
      peekAlt = first ? first.title : '';
      teaserTitle = first ? first.title : 'Your recommendations';
    } else {
      peekImg = this.images.hero || '';
    }

    var savedEmail = '';
    try {
      savedEmail = sessionStorage.getItem(STORAGE_EMAIL_KEY) || this.session.email || '';
    } catch (e) {
      savedEmail = this.session.email || '';
    }

    return (
      '<section class="af-gate' +
      (midQuiz ? ' af-gate--mid' : '') +
      '">' +
      '<button type="button" class="af-back" data-af-action="back" aria-label="Back">←</button>' +
      '<div class="af-gate__card">' +
      (peekImg
        ? '<div class="af-gate__peek">' +
          '<img src="' +
          escapeHtml(peekImg) +
          '" alt="' +
          escapeHtml(peekAlt) +
          '" decoding="async">' +
          '</div>'
        : '') +
      '<div class="af-gate__body">' +
      '<p class="af-eyebrow">Art Finder</p>' +
      (teaserTitle
        ? '<h2 class="af-heading af-heading--md">' + escapeHtml(teaserTitle) + '</h2>'
        : '') +
      '<p class="af-lede">' +
      escapeHtml(q.gate_heading) +
      '</p>' +
      '<p class="af-supporting">' +
      escapeHtml(q.gate_supporting) +
      '</p>' +
      '<form class="af-gate__form" data-af-gate novalidate>' +
      '<label class="af-field"><span>Email</span>' +
      '<input type="email" name="email" autocomplete="email" required value="' +
      escapeHtml(savedEmail) +
      '"></label>' +
      '<p class="af-error" data-af-error hidden></p>' +
      '<button type="submit" class="af-btn af-btn--primary">' +
      escapeHtml(q.gate_cta) +
      '</button>' +
      '<p class="af-privacy">' +
      escapeHtml(q.privacy) +
      '</p>' +
      '</form></div></div></section>'
    );
  };

  ArtFinderApp.prototype.renderArtCard = function (art) {
    var viz = visualizerUrl(
      this.routes,
      art.handle,
      art.size && art.size.size,
      art.medium && art.medium.key
    );
    return (
      '<article class="af-art af-art--' +
      escapeHtml(art.role || 'strongest') +
      '">' +
      '<p class="af-art__role">' +
      escapeHtml(art.role_label || '') +
      '</p>' +
      '<a class="af-art__link" href="' +
      escapeHtml(art.url) +
      '" data-af-action="track" data-af-event="art_finder_artwork_clicked" data-handle="' +
      escapeHtml(art.handle) +
      '">' +
      (art.image
        ? '<img class="af-art__img" src="' +
          escapeHtml(art.image) +
          '" alt="' +
          escapeHtml(art.title) +
          '" loading="lazy">'
        : '') +
      '<h3 class="af-art__title">' +
      escapeHtml(art.title) +
      '</h3></a>' +
      (art.mockup
        ? '<img class="af-art__mockup" src="' +
          escapeHtml(art.mockup) +
          '" alt="' +
          escapeHtml(art.title) +
          ' in a room" loading="lazy">'
        : '') +
      '<p class="af-art__meta">Suggested ' +
      escapeHtml((art.size && art.size.label) || '') +
      ' · ' +
      escapeHtml((art.medium && art.medium.label) || '') +
      '</p>' +
      '<p class="af-art__why">' +
      escapeHtml(art.why || '') +
      '</p>' +
      '<a class="af-art__viz" href="' +
      escapeHtml(viz) +
      '" data-af-action="track" data-af-event="art_finder_wall_visualizer_clicked" data-handle="' +
      escapeHtml(art.handle) +
      '">See it on a wall</a>' +
      '</article>'
    );
  };

  ArtFinderApp.prototype.renderResults = function () {
    var q = this.config.quiz;
    var rec =
      this.recommendation || this.computeRecommendation(!!this.session.relax_subject);
    this.recommendation = rec;

    if (!this._resultsTracked) {
      this._resultsTracked = true;
      var viewedArts = rec.artworks || [];
      trackArtFinder('art_finder_result_viewed', {
        subject_group: rec.subject_group || '',
        eligible_count: rec.eligible_count || 0,
        relax_subject: !!rec.relax_subject,
        space_type: (rec.quiz && rec.quiz.room) || '',
        mood: (rec.quiz && rec.quiz.mood) || '',
        palette: (rec.quiz && rec.quiz.palette) || '',
        statement: (rec.quiz && rec.quiz.statement) || '',
        rank_1: viewedArts[0] ? viewedArts[0].handle : '',
        rank_2: viewedArts[1] ? viewedArts[1].handle : '',
        rank_3: viewedArts[2] ? viewedArts[2].handle : ''
      });
    }

    if (rec.no_results) {
      return (
        '<section class="af-results af-results--empty">' +
        '<p class="af-eyebrow">Art Finder</p>' +
        '<h2 class="af-heading af-heading--lg">' +
        escapeHtml(q.no_results_heading) +
        '</h2>' +
        '<p class="af-lede">' +
        escapeHtml(q.no_results_supporting) +
        '</p>' +
        '<button type="button" class="af-btn af-btn--primary" data-af-action="relax">' +
        escapeHtml(q.no_results_cta) +
        '</button>' +
        '<p class="af-footer-links"><button type="button" class="af-text-btn" data-af-action="restart">Change my answers</button></p>' +
        '</section>'
      );
    }

    var cards = (rec.artworks || [])
      .map(
        function (art) {
          return this.renderArtCard(art);
        }.bind(this)
      )
      .join('');

    var primary = (rec.artworks || [])[0];
    var vizPrimary = primary
      ? visualizerUrl(this.routes, primary.handle, primary.size.size, primary.medium.key)
      : this.routes.visualizer || '/pages/visualize-your-space';

    var requestParams = new URLSearchParams();
    requestParams.set('source', 'art-finder');
    if (rec.subject_group) requestParams.set('art_finder_subject', rec.subject_group);
    var titles = (rec.artworks || [])
      .map(function (a) {
        return a.title;
      })
      .filter(Boolean)
      .join(', ');
    if (titles) requestParams.set('product_title', titles);
    if (primary && primary.url) requestParams.set('product_url', primary.url);
    else if (primary && primary.handle) {
      requestParams.set('product_url', '/products/' + primary.handle);
    }
    if (this.session.first_name) requestParams.set('first_name', this.session.first_name);
    if (this.session.email) requestParams.set('email', this.session.email);
    if (rec.quiz && rec.quiz.room) {
      requestParams.set('room_placement', String(rec.quiz.room).replace(/_/g, ' '));
    }
    if (primary && primary.size && primary.size.size) {
      requestParams.set('size_range', primary.size.size);
    }
    var requestUrl =
      (this.routes.request_info || '/pages/request-information') +
      '?' +
      requestParams.toString();

    return (
      '<section class="af-results">' +
      '<p class="af-eyebrow">Your recommendations</p>' +
      '<h2 class="af-heading af-heading--lg">' +
      escapeHtml(rec.headline) +
      '</h2>' +
      (rec.relax_subject
        ? '<p class="af-supporting">Subject filter relaxed — ranked by mood, palette, and room.</p>'
        : '') +
      '<div class="af-art-grid af-art-grid--roles">' +
      cards +
      '</div>' +
      '<div class="af-ctas">' +
      '<a class="af-btn af-btn--primary" href="' +
      escapeHtml(vizPrimary) +
      '" data-af-action="track" data-af-event="art_finder_wall_visualizer_clicked" data-handle="' +
      escapeHtml(primary ? primary.handle : '') +
      '">See it on a wall</a>' +
      '<a class="af-btn af-btn--ghost" href="' +
      escapeHtml(requestUrl) +
      '" data-af-action="track" data-af-event="art_finder_consultation_clicked">Ask Greg about your space</a>' +
      (this.showGuideBonus
        ? '<a class="af-btn af-btn--ghost" href="/pages/collector-guide">Also send the Collector Guide</a>'
        : '') +
      '</div>' +
      this.renderResultsSave(q) +
      '<p class="af-footer-links"><button type="button" class="af-text-btn" data-af-action="restart">Change my answers</button></p>' +
      '</section>'
    );
  };

  ArtFinderApp.prototype.renderResultsSave = function (q) {
    if (this.session.email) {
      return (
        '<p class="af-supporting af-results__saved">Picks saved to ' +
        escapeHtml(this.session.email) +
        '.</p>'
      );
    }
    var savedEmail = '';
    try {
      savedEmail = sessionStorage.getItem(STORAGE_EMAIL_KEY) || '';
    } catch (e) {}
    return (
      '<form class="af-gate__form af-results__save" data-af-gate novalidate>' +
      '<p class="af-lede">' +
      escapeHtml(q.gate_heading) +
      '</p>' +
      '<p class="af-supporting">' +
      escapeHtml(q.gate_supporting) +
      '</p>' +
      '<label class="af-field"><span>Email</span>' +
      '<input type="email" name="email" autocomplete="email" required value="' +
      escapeHtml(savedEmail) +
      '"></label>' +
      '<p class="af-error" data-af-error hidden></p>' +
      '<button type="submit" class="af-btn af-btn--ghost">' +
      escapeHtml(q.gate_cta) +
      '</button>' +
      '<p class="af-privacy">' +
      escapeHtml(q.privacy) +
      '</p>' +
      '</form>'
    );
  };

  ArtFinderApp.prototype.render = function () {
    var html = '';
    if (this.step === 'landing') html = this.renderLanding();
    else if (this.step === 'quiz') html = this.renderQuiz();
    else if (this.step === 'gate') html = this.renderGate();
    else if (this.step === 'results') html = this.renderResults();
    this.root.innerHTML = html;
    // innerHTML wipes focus — park on question label (quiz) or primary control.
    try {
      var focusEl = null;
      if (this.step === 'quiz') {
        focusEl = this.root.querySelector('#af-q-label');
        if (focusEl && !focusEl.hasAttribute('tabindex')) {
          focusEl.setAttribute('tabindex', '-1');
        }
      } else if (this.step === 'gate') {
        focusEl = this.root.querySelector('input[name="email"]');
      } else if (this.step === 'results') {
        focusEl = this.root.querySelector('.af-heading');
        if (focusEl && !focusEl.hasAttribute('tabindex')) {
          focusEl.setAttribute('tabindex', '-1');
        }
      }
      if (focusEl && typeof focusEl.focus === 'function') {
        focusEl.focus({ preventScroll: true });
      }
    } catch (e) {}
    try {
      this.root.scrollIntoView({ block: 'start', behavior: 'smooth' });
    } catch (e) {}
  };

  function suppressKlaviyoOnsitePopup() {
    function hideKlaviyoChrome() {
      var nodes = document.querySelectorAll(
        '[class*="kl-private"], [class*="kl-teaser"], [class*="klaviyo-form"], [id*="klaviyo-form"], .klaviyo-form-preview-wrapper'
      );
      for (var i = 0; i < nodes.length; i++) {
        nodes[i].style.setProperty('display', 'none', 'important');
        nodes[i].style.setProperty('visibility', 'hidden', 'important');
        nodes[i].style.setProperty('pointer-events', 'none', 'important');
        nodes[i].setAttribute('aria-hidden', 'true');
      }
      if (document.body) {
        document.body.classList.remove('klaviyo-prevent-body-scrolling');
        document.body.style.removeProperty('overflow');
      }
    }
    function closeForm(formId) {
      if (!formId) return;
      root._klOnsite = root._klOnsite || [];
      root._klOnsite.push(['closeForm', formId]);
    }
    root.addEventListener('klaviyoForms', function (e) {
      var type = e.detail && e.detail.type;
      var formId = e.detail && e.detail.formId;
      hideKlaviyoChrome();
      if (!formId) return;
      if (type === 'open' || type === 'embedOpen' || type === 'teaserOpen') {
        closeForm(formId);
        setTimeout(function () {
          closeForm(formId);
          hideKlaviyoChrome();
        }, 50);
        setTimeout(function () {
          closeForm(formId);
          hideKlaviyoChrome();
        }, 400);
      }
    });
    hideKlaviyoChrome();
    [0, 400, 1200, 3000, 6000, 10000].forEach(function (ms) {
      setTimeout(hideKlaviyoChrome, ms);
    });
    if (typeof MutationObserver === 'function' && document.documentElement) {
      var scheduled = false;
      var observer = new MutationObserver(function () {
        if (scheduled) return;
        scheduled = true;
        setTimeout(function () {
          scheduled = false;
          hideKlaviyoChrome();
        }, 30);
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  function bootFromDom() {
    var el = document.querySelector('[data-art-finder]');
    if (!el || el.__afBooted) return;
    var raw = document.getElementById('ArtFinderBoot');
    if (!raw) return;
    var boot;
    try {
      boot = JSON.parse(raw.textContent);
    } catch (e) {
      return;
    }
    if (!boot) return;
    if (!schema()) {
      el.innerHTML = '<p class="af-lede">Art Finder scoring could not load. Please refresh.</p>';
      return;
    }
    el.__afBooted = true;
    suppressKlaviyoOnsitePopup();

    function start(config, catalog) {
      boot.config = config;
      if (catalog && typeof catalog === 'object') {
        boot.catalog = catalog;
      }
      if (!boot.config || !boot.config.quiz) {
        el.innerHTML = '<p class="af-lede">Art Finder could not load. Please refresh.</p>';
        return;
      }
      if (!boot.catalog || !Object.keys(boot.catalog).length) {
        el.innerHTML = '<p class="af-lede">Art Finder catalog could not load. Please refresh.</p>';
        return;
      }
      root.__artFinderApp = new ArtFinderApp(el, boot);
    }

    var configPromise = boot.config
      ? Promise.resolve(boot.config)
      : boot.configUrl
        ? fetch(boot.configUrl, { credentials: 'same-origin' }).then(function (res) {
            if (!res.ok) throw new Error('config ' + res.status);
            return res.json();
          })
        : Promise.reject(new Error('config missing'));

    var catalogPromise =
      boot.catalog && Object.keys(boot.catalog).length
        ? Promise.resolve(boot.catalog)
        : boot.catalogUrl
          ? fetch(boot.catalogUrl, { credentials: 'same-origin' }).then(function (res) {
              if (!res.ok) throw new Error('catalog ' + res.status);
              return res.json();
            })
          : Promise.reject(new Error('catalog missing'));

    Promise.all([configPromise, catalogPromise])
      .then(function (pair) {
        start(pair[0], pair[1]);
      })
      .catch(function () {
        el.innerHTML = '<p class="af-lede">Art Finder could not load. Please refresh.</p>';
      });
  }

  root.ArtFinder = {
    answersToQuiz: answersToQuiz,
    resolveSize: resolveSize,
    buildRecommendation: buildRecommendation,
    boot: bootFromDom,
    HISTORY_KEY: HISTORY_KEY,
    hashForView: hashForView,
    viewFromHash: viewFromHash,
    readAfHistory: readAfHistory,
    writeAfHistory: writeAfHistory,
    sameAfHistoryView: sameAfHistoryView
  };

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bootFromDom);
    } else {
      bootFromDom();
    }
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = root.ArtFinder;
  }
})(typeof window !== 'undefined' ? window : globalThis);
